app.pages = app.pages || {};

/**
 * Downloads page handler with modal management and advanced filtering
 */
app.pages.downloads = {
    // Constants
    SELECTORS: {
        FILTER_LIST: 'ul.downloads-filter-list',
        FILTER_BUTTON: 'label label-dark icon-close icon-right',
        HEADLINE: '.downloads-list__headline'
    },

    ENDPOINTS: {
        DOWNLOAD_MODAL: '/app/downloads'
    },

    REGEX: {
        HASH_PATTERN: /^#?dlm-([a-zA-Z0-9_-]+)$/
    },

    /**
     * Initialize the downloads page functionality
     */
    init: async () => {
        const { downloads } = app.pages;
        
        // Initialize filtering system
        downloads.filter = app.filter.init({
            hash: 'dlf',
            loadingDelay: 1500,
            selectors: {
                ITEMS: '.downloads-list__item',
                NO_RESULTS: '.filter-message.no-results',
                LOADING: '.filter-message.loading'
            },
            filter: {
                logic: '(search AND category AND topic AND audience AND currency AND country)',
                fields: {
                    search: {
                        selector: '.downloads-search input[name="search"]',
                        type: 'text',
                        debounce: 300,
                        emptyValue: ''
                    },
                    category: {
                        selector: 'input[name="category[]"]',
                        type: 'checkbox',
                        emptyValue: ''
                    },
                    topic: {
                        selector: 'input[name="topic[]"]',
                        type: 'checkbox',
                        emptyValue: ''
                    },
                    audience: {
                        selector: 'input[name="audience[]"]',
                        type: 'checkbox',
                        emptyValue: ''
                    },
                    currency: {
                        selector: 'input[name="currency[]"]',
                        type: 'checkbox',
                        emptyValue: ''
                    },
                    country: {
                        selector: 'input[name="country[]"]',
                        type: 'checkbox',
                        emptyValue: ''
                    }
                }
            },
            on: {
                beforeFilter: (filterState) => {
                    document.querySelector(downloads.SELECTORS.HEADLINE).style.display = 'none';
                    downloads.updateFilterLabels(filterState);
                },
                filter: (visibleCount, totalCount, filterState) => {
                    document.querySelector(downloads.SELECTORS.HEADLINE).style.display = visibleCount > 0 ? '' : 'none';
                }
            }
        });

        // Initialize modal handling
        downloads.initModals();
    },

    /**
     * Initialize modal functionality
     */
    initModals: () => {
        const { downloads } = app.pages;
        
        // Delegated click handler for dynamic modal links
        document.addEventListener('click', async (event) => {
            const link = event.target.closest('a[href^="#dlm-"]');
            if (!link) return;

            event.preventDefault();
            const href = link.getAttribute('href');
            const productId = downloads.extractProductId(href);
            if (productId) {
                await downloads.modal.open(productId);
            }
        });

        // Handle hash on page load
        const productId = downloads.extractProductId();
        if (productId) {
            downloads.modal.open(productId);
        }
    },

    /**
     * Extract product ID from URL hash
     * @param {string} [hash=window.location.hash] - URL hash fragment
     * @returns {string|null} Decoded product ID or null if not found
     */
    extractProductId: (hash = window.location.hash) => {
        const { downloads } = app.pages;
        
        // Check HashManager state first
        if (window.HashManager?.state) {
            for (const [key, value] of Object.entries(window.HashManager.state)) {
                if (key.startsWith('dlm-') && value === true) {
                    return decodeURIComponent(key.slice(4));
                }
            }
        }

        // Fallback to regex parsing
        const matches = hash.match(downloads.REGEX.HASH_PATTERN);
        return matches?.[1] ? decodeURIComponent(matches[1]) : null;
    },

    /**
     * Update filter labels in the UI
     * @param {Object} filterState - Current filter state
     */
    updateFilterLabels: (filterState) => {
        const { downloads } = app.pages;
        const filterList = document.querySelector(downloads.SELECTORS.FILTER_LIST);
        if (!filterList) return;

        // Clear existing labels
        filterList.innerHTML = '';

        // Create labels for active filters
        Object.entries(filterState).forEach(([fieldName, value]) => {
            if (fieldName === 'search' && value) {
                const searchInput = document.querySelector(`[name="${fieldName}"]`);
                if (searchInput) {
                    const label = searchInput.getAttribute('data-label') + value;
                    downloads.createFilterLabel(filterList, fieldName, value, label);
                }
            } else if (Array.isArray(value) && value.length > 0) {
                value.forEach(val => {
                    const labelText = downloads.getCheckboxLabelText(fieldName, val);
                    downloads.createFilterLabel(filterList, fieldName, val, labelText);
                });
            }
        });
    },

    /**
     * Get the display text for a checkbox value
     * @param {string} fieldName - Field name (category, topic, etc.)
     * @param {string} value - Checkbox value
     * @returns {string} Display text
     */
    getCheckboxLabelText: (fieldName, value) => {
        const checkbox = document.querySelector(`input[name="${fieldName}[]"][value="${value}"]`);
        if (checkbox && checkbox.nextElementSibling) {
            return checkbox.nextElementSibling.textContent || value;
        }
        return value;
    },

    /**
     * Create a filter label element
     * @param {HTMLElement} filterList - Container for filter labels
     * @param {string} fieldName - Field name
     * @param {string} value - Filter value
     * @param {string} displayText - Text to display
     */
    createFilterLabel: (filterList, fieldName, value, displayText) => {
        const { downloads } = app.pages;
        const li = document.createElement('li');
        
        li.className = downloads.SELECTORS.FILTER_BUTTON;
        li.setAttribute('data-field', fieldName);
        li.setAttribute('data-value', value);
        li.textContent = displayText;

        li.addEventListener('click', () => {
            downloads.removeFilter(fieldName, value);
        });

        filterList.appendChild(li);
    },

    /**
     * Remove a specific filter
     * @param {string} fieldName - Field name to remove filter from
     * @param {string} value - Specific value to remove
     */
    removeFilter: (fieldName, value) => {
        const { downloads } = app.pages;
        
        if (fieldName === 'search') {
            // Clear search input
            const searchInput = document.querySelector('.downloads-search input[name="search"]');
            if (searchInput) {
                searchInput.value = '';
            }
        } else {
            // Uncheck specific checkbox
            const checkbox = document.querySelector(`input[name="${fieldName}[]"][value="${value}"]`);
            if (checkbox) {
                checkbox.checked = false;
            }
        }

        // Trigger filter update
        if (downloads.filter) {
            downloads.filter.updateStateFromFields();
            downloads.filter.applyFilters();
        }
    },

    /**
     * Modal management functionality
     */
    modal: {
        /**
         * Open a download modal for the given product
         * @param {string} productId - The product identifier
         */
        open: async (productId) => {
            if (!productId) {
                app.notifications.show('warn', 'Modal open called without productId');
                return;
            }

            const { downloads } = app.pages;
            const modalId = `dlm-${productId}`;
            let el = document.getElementById(modalId);

            if (!el) {
                el = await downloads.modal.create(productId, modalId);
                if (!el) return; // Creation failed
            }

            app.ui.modal.open(modalId);
        },

        /**
         * Create a new modal element
         * @param {string} productId - The product identifier
         * @param {string} modalId - The modal DOM ID
         * @returns {HTMLElement|null} The created modal element or null if failed
         */
        create: async (productId, modalId) => {
            const { downloads } = app.pages;
            
            try {
                const html = await app.request.post(downloads.ENDPOINTS.DOWNLOAD_MODAL, {
                    productId,
                    language: app.data.defaults.language
                });

                if (!downloads.modal.validateResponse(html)) {
                    return null;
                }

                const el = downloads.modal.createElementFromHTML(html, modalId);
                if (!el) return null;

                document.body.appendChild(el);
                await app.ui.modal.create(modalId);
                
                return el;

            } catch (error) {
                app.notifications.show('error', 'Failed to load modal:', error);
                app.notifications.show('user', 'Unable to load download details. Please try again.');
                return null;
            }
        },

        /**
         * Validate server response
         * @param {*} html - Server response
         * @returns {boolean} True if valid HTML response
         */
        validateResponse: (html) => {
            if (typeof html === 'object') {
                if (html.status === 'error' || html.error) {
                    app.notifications.show('warn', 'Server error:', html.message || html.error || html);
                    return false;
                }
                app.notifications.show('warn', 'Expected HTML, got object:', html);
                return false;
            }
            return typeof html === 'string';
        },

        /**
         * Create DOM element from HTML string
         * @param {string} html - HTML content
         * @param {string} modalId - Modal ID to assign
         * @returns {HTMLElement|null} Created element or null
         */
        createElementFromHTML: (html, modalId) => {
            const wrapper = document.createElement('div');
            wrapper.innerHTML = html.trim();
            const el = wrapper.firstElementChild;

            if (!el) {
                app.notifications.show('warn', 'No modal element returned from server.');
                return null;
            }

            el.id = el.id || modalId;
            return el;
        }
    }
};